import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { Loading, PageHeader, EmptyState } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";

const TYPES = ["", "lead_created", "customer_registered", "onboarding_completed", "assessment_completed",
  "payment_received", "cm_assigned", "visit_started", "visit_completed", "service_request_created",
  "incident_urgent", "sos_emergency", "sla_escalation", "wa_connected"];

const PRIORITY_STYLE = {
  emergency: "bg-red-100 text-red-700",
  high: "bg-amber-100 text-amber-800",
  normal: "bg-stone-100 text-stone-600",
};

const CHANNEL_STATUS = {
  simulated_sent: "Simulated (TEST)",
  skipped_no_phone: "No phone on record",
  skipped_not_connected: "WhatsApp not activated",
  skipped_prefs: "Off in preferences",
  skipped_no_email: "No email on record",
  sent: "Sent",
  delivered: "Delivered",
  failed: "Failed",
};

const recipientLabel = (n) =>
  n.recipient_kind === "family" ? "Family (customer)"
  : n.recipient_kind === "cm" ? "Care Manager"
  : n.recipient_role === "ops_manager" ? "Ops Manager"
  : n.recipient_role === "super_admin" ? "Super Admin"
  : (n.recipient_role || n.recipient_kind);

function ChannelChip({ c }) {
  const ok = ["sent", "delivered", "simulated_sent"].includes(c.status);
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${ok ? "bg-emerald-50 text-emerald-800" : c.status.startsWith("skipped") ? "bg-stone-100 text-stone-400" : "bg-red-50 text-red-700"}`}>
      {c.channel}: {CHANNEL_STATUS[c.status] || c.status}
    </span>
  );
}

export default function AdminNotifications() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("");
  const [busy, setBusy] = useState(false);
  const canAck = ["ops_manager", "admin", "super_admin"].includes(user?.role);
  const load = () => api.get(`/admin/notifications${type ? `?type=${type}` : ""}`)
    .then((r) => setRows(r.data)).catch((e) => { toast.error(formatErr(e)); setRows([]); });
  useEffect(() => { load(); }, [type]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rows) return <Loading />;

  const act = async (id, action) => {
    setBusy(true);
    try { await api.post(`/admin/notifications/${id}/${action}`, {}); toast.success(action === "ack" ? "Acknowledged" : "Marked action completed"); await load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-notifications">
      <PageHeader title="Notifications" sub="Every operational event — channel delivery status, acknowledgement and action history." />
      <div className="mb-4 flex items-center gap-3">
        <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-xl border border-stone-200 px-3 py-2 text-sm" data-testid="notif-type-filter">
          {TYPES.map((t) => <option key={t} value={t}>{t ? t.replace(/_/g, " ") : "All event types"}</option>)}
        </select>
        <span className="text-xs text-stone-400">{rows.length} events</span>
      </div>
      {rows.length === 0 && <EmptyState title="No notifications yet" body="Operational events appear here as they happen." />}
      {(() => {
        const groups = [];
        const seen = {};
        for (const n of rows) {
          const key = n.ref_id ? `${n.type}:${n.ref_id}` : n.id;
          if (!seen[key]) {
            seen[key] = { key, type: n.type, title: n.title, body: n.body, priority: n.priority,
                          created_at: n.created_at, action_url: n.action_url, action_label: n.action_label,
                          escalated: false, deliveries: [] };
            groups.push(seen[key]);
          }
          seen[key].deliveries.push(n);
          if (n.escalated) seen[key].escalated = true;
        }
        return groups.map((g) => (
          <div key={g.key} className="p1-card p-4" data-testid={`notif-event-${g.key}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-stone-900">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${PRIORITY_STYLE[g.priority] || PRIORITY_STYLE.normal}`}>{g.priority}</span>
                  {g.title}
                  {g.escalated && <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-700">ESCALATED</span>}
                </p>
                <p className="mt-0.5 text-xs text-stone-500">{g.body}</p>
                <p className="mt-1 text-[11px] text-stone-400">{g.type.replace(/_/g, " ")} · {g.created_at?.slice(0, 16).replace("T", " ")} IST · one event, {g.deliveries.length} recipient {g.deliveries.length === 1 ? "delivery" : "deliveries"}</p>
              </div>
              {g.action_url && <Link to={g.action_url} className="shrink-0 rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 hover:border-emerald-700" data-testid={`notif-open-${g.key}`}>{g.action_label || "Open"}</Link>}
            </div>
            <div className="mt-3 border-t border-stone-100 pt-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-stone-400">Recipients & delivery</p>
              <div className="mt-1.5 space-y-1.5">
                {g.deliveries.map((n) => (
                  <div key={n.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-stone-50 px-3 py-2" data-testid={`notif-delivery-${n.id}`}>
                    <span className="text-xs font-semibold text-stone-700">{recipientLabel(n)}</span>
                    <span className="flex flex-wrap gap-1">{(n.channels || []).map((c, i) => <ChannelChip key={i} c={c} />)}</span>
                    <span className="flex items-center gap-2 text-[11px] text-stone-500" data-testid={`notif-status-${n.id}`}>
                      <strong>{n.status.replace(/_/g, " ")}</strong>
                      {n.ack_by_name && <> · ack {n.ack_by_name} {n.ack_at?.slice(5, 16).replace("T", " ")}</>}
                      {n.completed_by_name && <> · done {n.completed_by_name}</>}
                      {canAck && n.status === "open" && (
                        <button onClick={() => act(n.id, "ack")} disabled={busy} className="rounded-full bg-emerald-900 px-2.5 py-0.5 text-[10px] font-bold text-white" data-testid={`notif-ack-${n.id}`}>Acknowledge</button>
                      )}
                      {canAck && n.status === "acknowledged" && (
                        <button onClick={() => act(n.id, "complete")} disabled={busy} className="rounded-full border border-emerald-800 px-2.5 py-0.5 text-[10px] font-bold text-emerald-900" data-testid={`notif-complete-${n.id}`}>Mark done</button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ));
      })()}
    </div>
  );
}
